import { useTenantConfig } from 'utils/hooks/useTenantConfig';
import { TenantFeatureModel, TenantSubFeatureModel } from '@utmost.co/enterprise-models';
import { CustomTenantModel } from 'src/providers/Tenant/Tenant.types';
import { CustomFeaturesModel, FeaturesNames } from 'src/definitions/custom-models';

const getFeaturesSelector = (tenantConfig: CustomTenantModel | undefined) =>
  tenantConfig?.features ?? ({} as CustomFeaturesModel);

const isEnabled = (featureId?: FeaturesNames, featureFlags?: CustomFeaturesModel, falseIfDoesNotExist = false) => {
  if (!featureId || featureFlags?.[featureId] === undefined) return !falseIfDoesNotExist;
  return featureFlags?.[featureId]?.enabled === true;
};

export const useFeatureFlags = (
  falseIfDoesNotExist: boolean = false
): {
  isEnabled(featureName: FeaturesNames): boolean;
  getFeature(featureName: FeaturesNames): TenantFeatureModel;
  getSubFeature(featureName: FeaturesNames, subFeatureName: string): null | TenantSubFeatureModel;
  isEnabledSubFeature(featureName: FeaturesNames, subFeatureName: string): boolean;
  isEnabledSecondLevelSubFeature(
    featureName: FeaturesNames,
    subFeatureName: string,
    subSubFeatureName: string
  ): boolean;
  isDependencyEnabled(item?: { config?: { dependent?: string } | null }): boolean;
  features: CustomFeaturesModel;
} => {
  const tenantConfig = useTenantConfig();
  const features = getFeaturesSelector(tenantConfig);

  const isFeatureEnabled = (featureName: FeaturesNames): boolean =>
    isEnabled(featureName, features, falseIfDoesNotExist) && isDependencyEnabled(features[featureName]);

  const isEnabledSubFeature = (featureName: FeaturesNames, subFeatureName: string): boolean => {
    try {
      const subFeature = features[featureName].orderedFeatures.find((f) => f.name === subFeatureName);
      return (
        isFeatureEnabled(featureName) &&
        (subFeature === undefined || subFeature?.enabled) &&
        isDependencyEnabled(subFeature)
      );
    } catch (e) {
      return false;
    }
  };

  const isEnabledSecondLevelSubFeature = (
    featureName: FeaturesNames,
    subFeatureName: string,
    subSubFeatureName: string
  ): boolean => {
    try {
      const subFeature = features[featureName].orderedFeatures.find((f) => f.name === subFeatureName);
      const subSubFeature = subFeature?.orderedFeatures?.find((f) => f.name === subSubFeatureName);
      return (
        isEnabledSubFeature(featureName, subFeatureName) &&
        (subSubFeature === undefined || !!subSubFeature?.enabled) &&
        isDependencyEnabled(subSubFeature)
      );
    } catch (e) {
      return false;
    }
  };

  const getFeature = (featureName: FeaturesNames) => features[featureName];

  const getSubFeature = (featureName: FeaturesNames, subFeatureName: string) => {
    const subFeature = features[featureName]?.orderedFeatures?.find((f) => f.name === subFeatureName);
    if (!isFeatureEnabled(featureName) || subFeature === undefined || !subFeature.enabled) return null;
    return subFeature;
  };

  const isDependencyEnabled = (item?: { config?: { dependent?: string } | null }): boolean => {
    if (!item || !item.config || !item.config.dependent) {
      return true;
    }
    return isEnabled(item.config.dependent as FeaturesNames, features, falseIfDoesNotExist);
  };

  return {
    isEnabled: isFeatureEnabled,
    isEnabledSubFeature,
    isEnabledSecondLevelSubFeature,
    isDependencyEnabled,
    getSubFeature,
    getFeature,
    features
  };
};
